// src/pages/talleres/editar/ModalidadesPago.jsx
import React, { useEffect } from 'react';
import { Form, Checkbox, InputNumber, Card, Space, Tag, Alert, Typography } from 'antd';
import { InfoCircleOutlined, WarningOutlined, LockOutlined } from '@ant-design/icons';
import { PLANES } from './constants';
import { formatCLP, parseCLP } from './utils/tallerUtils';

const { Text } = Typography;

// Campos de valores SIN matrícula
const CAMPOS_VALORES = [
  { 
    key: 'valorParticular', 
    label: 'Particular', 
    required: true,
    description: 'Precio base para público general'
  },
  { 
    key: 'valorVecino', 
    label: 'Vecino', 
    required: false,
    description: 'Precio especial para vecinos de Ñuñoa'
  },
  { 
    key: 'valorAdultoMayor', 
    label: 'Adulto Mayor', 
    required: false,
    description: 'Precio especial para adultos mayores'
  },
  { 
    key: 'valorFuncionarios', 
    label: 'Funcionarios', 
    required: false,
    description: 'Precio especial para funcionarios municipales'
  }
];

export default function ModalidadesPago({ 
  form, 
  planActive, 
  setPlanActive, 
  disabled = false,
  planesPermitidos = ['mensual', 'trimestral', 'semestral', 'anual']
}) {
  
  console.log('🔍 DEBUG ModalidadesPago:', {
    planesPermitidos,
    planActive
  });

  // Desactivar automáticamente planes no permitidos
  useEffect(() => {
    const planesADesactivar = Object.keys(planActive).filter(
      plan => planActive[plan] && !planesPermitidos.includes(plan)
    );

    if (planesADesactivar.length > 0) {
      console.log('🔄 Desactivando planes no permitidos:', planesADesactivar);
      const nuevosPlanesActivos = { ...planActive };
      planesADesactivar.forEach(plan => {
        nuevosPlanesActivos[plan] = false;
      });
      setPlanActive(nuevosPlanesActivos);
    }
  }, [planesPermitidos, planActive, setPlanActive]);

  return (
    <div className="space-y-3">
      {/* Alert informativa principal */}
      <Alert
        message="Configura los planes de pago"
        description={
          <div className="space-y-2">
            <p>Selecciona los planes disponibles y define sus precios. El valor particular es obligatorio para cada plan seleccionado.</p>
            <p className="text-xs font-medium mt-2">
              ℹ️ La matrícula del recinto (si existe) se aplicará automáticamente según la configuración global del recinto.
            </p>
          </div>
        }
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        className="mb-4"
      />

      {PLANES.map((plan) => {
        const isActive = planActive[plan.value];
        const isPlanPermitido = planesPermitidos.includes(plan.value);
        const isDisabled = disabled || !isPlanPermitido;

        return (
          <Card
            key={plan.value}
            size="small"
            title={
              <Space>
                <Checkbox
                  checked={isActive}
                  onChange={(e) => {
                    if (isPlanPermitido) {
                      setPlanActive(prev => ({
                        ...prev,
                        [plan.value]: e.target.checked
                      }));
                    }
                  }}
                  disabled={isDisabled}
                >
                  <span className="font-medium">{plan.label}</span>
                </Checkbox>
                {!isPlanPermitido && (
                  <Tag icon={<LockOutlined />} color="default">
                    No disponible
                  </Tag>
                )}
              </Space>
            }
            className={!isPlanPermitido ? 'opacity-50' : ''}
          >
            {!isPlanPermitido ? (
              <Alert
                message="Plan no disponible"
                description="Este plan requiere que el taller tenga una duración mayor. Ajusta las fechas de actividades para habilitarlo."
                type="warning"
                showIcon
                size="small"
              />
            ) : !isActive ? (
              <Text type="secondary" className="text-sm block py-2">
                Marca el checkbox para configurar precios de este plan
              </Text>
            ) : (
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {CAMPOS_VALORES.map((campo) => (
                    <Form.Item
                      key={campo.key}
                      label={
                        <Space size={4}>
                          <span title={campo.description}>
                            {campo.label}
                          </span>
                          {campo.required && <Tag color="red" size="small">*</Tag>}
                        </Space>
                      }
                      name={['valores', plan.value, campo.key]}
                      rules={campo.required ? [
                        { required: true, message: 'Requerido' },
                        { 
                          validator: (_, value) => {
                            if (value && value <= 0) {
                              return Promise.reject('Debe ser mayor a 0');
                            }
                            return Promise.resolve();
                          }
                        }
                      ] : [
                        { 
                          validator: (_, value) => {
                            if (value && value < 0) {
                              return Promise.reject('No puede ser negativo');
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                      className="mb-0"
                      tooltip={campo.description}
                    >
                      <InputNumber
                        size="middle"
                        className="w-full"
                        min={0}
                        formatter={formatCLP}
                        parser={parseCLP}
                        prefix="$"
                        placeholder="0"
                        disabled={disabled}
                      />
                    </Form.Item>
                  ))}
                </div>

                {/* Info adicional sobre precios */}
                <div className="mt-2 p-2 bg-gray-50 rounded">
                  <Text type="secondary" className="text-xs">
                    💡 <strong>Tip:</strong> Los valores opcionales (Vecino, Adulto Mayor, Funcionarios) solo se mostrarán si son menores al precio particular.
                  </Text>
                </div>
              </div>
            )}
          </Card>
        );
      })}

      {/* Validación: al menos un plan seleccionado */}
      {(() => {
        const planesActivos = Object.values(planActive).filter(Boolean).length;

        if (planesActivos === 0) {
          return (
            <Alert
              message="Selecciona al menos un plan"
              description="Debes configurar al menos una modalidad de pago para continuar"
              type="warning"
              showIcon
              icon={<WarningOutlined />}
            />
          );
        }

        return (
          <Alert
            message={`${planesActivos} plan${planesActivos > 1 ? 'es' : ''} configurado${planesActivos > 1 ? 's' : ''}`}
            type="success"
            showIcon
          />
        );
      })()}

      {/* Info sobre descuentos y matrícula */}
      <div className="space-y-2">
        <div className="text-center p-3 bg-blue-50 rounded">
          <Text type="secondary" className="text-xs">
            💡 Los descuentos automáticos se aplicarán según las reglas del sistema
          </Text>
        </div>

        <div className="text-center p-3 bg-purple-50 rounded">
          <Text type="secondary" className="text-xs">
            🎫 Si el recinto tiene matrícula configurada y el taller la requiere, se aplicará automáticamente al momento de la inscripción
          </Text>
        </div>
      </div>
    </div>
  );
}
